import React from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonProps,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Link,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  OutreachCategory,
  type YouTubeChannelSearchDetails,
} from "../../types/outreach";

interface YouTubeChannelDetailProps {
  channel: YouTubeChannelSearchDetails | null;
  loading: boolean;
  onAddToReactionChannels: (
    channel: YouTubeChannelSearchDetails
  ) => void;
  onAddToPlaylistingChannels: (
    channel: YouTubeChannelSearchDetails
  ) => void;
  reactionChannelExists: boolean;
  playlistingChannelExists: boolean;
  addingToReaction: boolean;
  addingToPlaylisting: boolean;
  preferredCategory?: OutreachCategory;
}

const formatNumber = (num: number | string): string => {
  const parsedNum = typeof num === "string" ? parseInt(num, 10) : num;
  if (Number.isNaN(parsedNum)) return "0";

  if (parsedNum >= 1_000_000) {
    return `${(parsedNum / 1_000_000).toFixed(1)}M`;
  }

  if (parsedNum >= 1_000) {
    return `${(parsedNum / 1_000).toFixed(1)}K`;
  }

  return parsedNum.toLocaleString();
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getChannelUrl = ({
  channelId,
  customUrl,
}: Pick<
  YouTubeChannelSearchDetails,
  "channelId" | "customUrl"
>): string => {
  if (customUrl) {
    const trimmed = customUrl.trim();

    if (trimmed.startsWith("http")) {
      return trimmed;
    }

    if (trimmed.includes("youtube.com")) {
      return `https://${trimmed.replace(/^\/+/, "")}`;
    }

    if (trimmed.startsWith("@")) {
      return `https://www.youtube.com/${trimmed}`;
    }

    const normalized = trimmed.replace(/^\/+/, "");
    if (/^(channel|c|user)\//i.test(normalized)) {
      return `https://www.youtube.com/${normalized}`;
    }

    return `https://www.youtube.com/c/${normalized}`;
  }

  return `https://www.youtube.com/channel/${channelId}`;
};

export const YouTubeChannelDetail: React.FC<YouTubeChannelDetailProps> = ({
  channel,
  loading,
  onAddToReactionChannels,
  onAddToPlaylistingChannels,
  reactionChannelExists,
  playlistingChannelExists,
  addingToReaction,
  addingToPlaylisting,
  preferredCategory = OutreachCategory.REACTION_CHANNELS,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (loading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Skeleton
            variant="circular"
            width={120}
            height={120}
            sx={{ mx: "auto", my: 2 }}
          />
          <Skeleton
            variant="text"
            height={40}
            width="80%"
            sx={{ mx: "auto" }}
          />
          <Skeleton
            variant="text"
            height={30}
            width="60%"
            sx={{ mx: "auto", mb: 2 }}
          />
          <Divider sx={{ my: 2 }} />
          <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" height={20} />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" height={20} />
            </Grid>
          </Grid>
          <Skeleton variant="text" height={30} sx={{ mt: 2 }} />
          <Skeleton variant="text" height={20} />
        </CardContent>
        <CardActions>
          <Skeleton variant="rectangular" height={40} width="100%" />
        </CardActions>
      </Card>
    );
  }

  if (!channel) {
    return (
      <Card
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CardContent>
          <Typography variant="body1" color="text.secondary" align="center">
            Select a channel to view details
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const handleAddToReactionChannels = () => {
    onAddToReactionChannels(channel);
  };

  const handleAddToPlaylistingChannels = () => {
    onAddToPlaylistingChannels(channel);
  };

  const description = channel.description ?? "";
  const truncatedDescription = expanded
    ? description
    : description.length > 150
    ? `${description.substring(0, 150)}...`
    : description;
  const channelUrl = getChannelUrl(channel);

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1, overflowY: "auto" }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <Avatar
            src={channel.thumbnails.high.url}
            alt={channel.title}
            sx={{ width: 120, height: 120, mb: 2 }}
          />
          <Typography variant="h5" align="center" gutterBottom>
            <Link
              href={channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
            >
              {channel.title}
            </Link>
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" align="center">
            {`${formatNumber(channel.subscriberCount)} subscribers`}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" paragraph>
          {truncatedDescription}
          {description.length > 150 && (
            <Link
              component="button"
              variant="body2"
              onClick={() => setExpanded((prev) => !prev)}
              sx={{ ml: 1 }}
            >
              {expanded ? "Show less" : "Show more"}
            </Link>
          )}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="h6">
              {formatNumber(channel.viewCount)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Views
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">
              {formatNumber(channel.videoCount)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Videos
            </Typography>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Typography variant="body2">
            <strong>Email:</strong> {channel.email ?? "No email available"}
          </Typography>
          <Typography variant="body2">
            <strong>Published:</strong> {formatDate(channel.publishedAt)}
          </Typography>
          {channel.country && (
            <Typography variant="body2">
              <strong>Country:</strong> {channel.country}
            </Typography>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, flexDirection: "column", gap: 1 }}>
        {[
          preferredCategory === OutreachCategory.PLAYLISTING_CHANNELS
            ? OutreachCategory.PLAYLISTING_CHANNELS
            : OutreachCategory.REACTION_CHANNELS,
          preferredCategory === OutreachCategory.PLAYLISTING_CHANNELS
            ? OutreachCategory.REACTION_CHANNELS
            : OutreachCategory.PLAYLISTING_CHANNELS,
        ].map((category) => {
          const isReaction = category === OutreachCategory.REACTION_CHANNELS;
          const exists = isReaction
            ? reactionChannelExists
            : playlistingChannelExists;
          const isAdding = isReaction ? addingToReaction : addingToPlaylisting;
          const onClick: () => void = isReaction
            ? handleAddToReactionChannels
            : handleAddToPlaylistingChannels;
          const label = isReaction
            ? "Add to Reaction Channels"
            : "Add to Playlisting Channels";
          const tooltip = exists
            ? isReaction
              ? "Channel already exists in Reaction Channels"
              : "Channel already exists in Playlisting Channels"
            : "";
          const color: ButtonProps["color"] =
            category === preferredCategory ? "primary" : "secondary";

          return (
            <Tooltip key={category} title={tooltip}>
              <span style={{ width: "100%" }}>
                <Button
                  variant="contained"
                  color={color}
                  fullWidth
                  onClick={onClick}
                  disabled={exists || isAdding}
                >
                  {isAdding ? "Adding..." : label}
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </CardActions>
    </Card>
  );
};
